import { useQuery } from '@tanstack/react-query';
import { getShopCategories } from '../api/endpoints';
import { shopKeys } from './queryKeys';
import type { ShopCategory } from '../types';

export const useShopCategories = () =>
  useQuery<ShopCategory[]>({
    queryKey: shopKeys.categories,
    // No `signal`: see the note in shopClient.ts ShopFetchInit.
    queryFn: () => getShopCategories(),
    staleTime: 30 * 60_000,
    gcTime: 60 * 60_000,
    retry: 2,
  });

/**
 * Resolves a category slug to its record by selecting from the *same* cached
 * categories query — no second network request.
 */
export const useShopCategoryBySlug = (slug: string | undefined) => {
  const query = useQuery<ShopCategory[], Error, ShopCategory | undefined>({
    queryKey: shopKeys.categories,
    // No `signal`: see the note in shopClient.ts ShopFetchInit.
    queryFn: () => getShopCategories(),
    staleTime: 30 * 60_000,
    gcTime: 60 * 60_000,
    retry: 2,
    select: (categories) => categories.find((category) => category.slug === slug),
    enabled: Boolean(slug),
  });

  return {
    category: query.data,
    isPending: query.isPending,
    isError: query.isError,
    error: query.error,
    /** True once the list loaded successfully but this slug wasn't in it. */
    isNotFound: query.isSuccess && !query.data,
  };
};

export default useShopCategories;
